import type {
  GameConfig,
  Player,
  PlayerId,
  PlayerOrTeamId,
  Round,
  Session,
  Team,
  TeamId,
  TeamScoringType,
} from '@/schemas';
import { getTotalScore, getTeamScore } from './scoring';

/**
 * Find highest scoring players/teams from a scores map
 */
const findHighestScorers = (
  scores: Map<PlayerOrTeamId, number>,
): {
  winners: PlayerOrTeamId[];
  maxValue: number;
} => {
  if (scores.size === 0) {
    return { winners: [], maxValue: -Infinity };
  }

  let maxValue = -Infinity;
  const winners: PlayerOrTeamId[] = [];

  for (const [id, score] of scores) {
    if (score > maxValue) {
      maxValue = score;
      winners.length = 0;
      winners.push(id);
    } else if (score === maxValue) {
      winners.push(id);
    }
  }

  return { winners, maxValue };
};

/**
 * Calculate scores for mostPoints win condition
 */
const calculateMostPointsScores = (
  session: Session,
  gameDefinition: GameConfig,
): Map<PlayerOrTeamId, number> => {
  const scores = new Map<PlayerOrTeamId, number>();

  if (gameDefinition.teamScoringType && session.teams) {
    // Team scoring
    for (const team of session.teams) {
      const teamScore = getTeamScore(session, team.id, gameDefinition.teamScoringType);
      scores.set(team.id, teamScore);
    }
  } else {
    // Individual scoring
    for (const player of session.players) {
      if (!player.deletedAt) {
        const totalScore = getTotalScore(session, player.id);
        scores.set(player.id, totalScore);
      }
    }
  }

  return scores;
};

/**
 * Find round winners (players with highest score in a round)
 */
export const getRoundWinners = (round: Round, players: Player[]): PlayerId[] => {
  let maxScore = -Infinity;
  const winners: PlayerId[] = [];

  for (const player of players) {
    if (player.deletedAt) continue;
    const score = round.scores[player.id] ?? 0;
    if (score > maxScore) {
      maxScore = score;
      winners.length = 0;
      winners.push(player.id);
    } else if (score === maxScore) {
      winners.push(player.id);
    }
  }

  return winners;
};

/**
 * Calculate team score for a round (combined mode)
 */
const getTeamRoundScore = (round: Round, team: Team): number => {
  let total = 0;
  for (const playerId of team.playerIds) {
    total += round.scores[playerId] ?? 0;
  }
  return total;
};

/**
 * Check if a team won a round (mostRounds, individual scoring)
 */
const didTeamWinRoundIndividual = (round: Round, team: Team, session: Session): boolean => {
  const roundWinners = getRoundWinners(round, session.players);
  return team.playerIds.some((pid) => roundWinners.includes(pid));
};

/**
 * Check if a team won a round (mostRounds, combined scoring)
 */
const didTeamWinRoundCombined = (round: Round, team: Team, session: Session): boolean => {
  const teamScore = getTeamRoundScore(round, team);

  if (!session.teams) return false;

  // Calculate all team scores
  let maxTeamScore = -Infinity;
  const winningTeams: TeamId[] = [];

  for (const t of session.teams) {
    const tScore = getTeamRoundScore(round, t);
    if (tScore > maxTeamScore) {
      maxTeamScore = tScore;
      winningTeams.length = 0;
      winningTeams.push(t.id);
    } else if (tScore === maxTeamScore) {
      winningTeams.push(t.id);
    }
  }

  return teamScore === maxTeamScore && winningTeams.includes(team.id);
};

/**
 * Calculate round wins for a team (mostRounds win condition)
 */
const calculateTeamRoundWins = (
  session: Session,
  team: Team,
  teamScoringType: TeamScoringType,
): number => {
  let roundWins = 0;

  for (const round of session.rounds) {
    if (round.deletedAt || !round.completedAt) continue;

    const teamWon =
      teamScoringType === 'combined'
        ? didTeamWinRoundCombined(round, team, session)
        : didTeamWinRoundIndividual(round, team, session);

    if (teamWon) {
      roundWins += 1;
    }
  }

  return roundWins;
};

/**
 * Calculate round wins for a player (mostRounds win condition)
 */
const calculatePlayerRoundWins = (session: Session, playerId: PlayerId): number => {
  let roundWins = 0;

  for (const round of session.rounds) {
    if (round.deletedAt || !round.completedAt) continue;

    const roundWinners = getRoundWinners(round, session.players);
    if (roundWinners.includes(playerId)) {
      roundWins += 1;
    }
  }

  return roundWins;
};

/**
 * Calculate scores for mostRounds win condition
 */
const calculateMostRoundsScores = (
  session: Session,
  gameDefinition: GameConfig,
): Map<PlayerOrTeamId, number> => {
  const scores = new Map<PlayerOrTeamId, number>();

  if (gameDefinition.teamScoringType && session.teams) {
    // Team scoring
    for (const team of session.teams) {
      const roundWins = calculateTeamRoundWins(session, team, gameDefinition.teamScoringType);
      scores.set(team.id, roundWins);
    }
  } else {
    // Individual scoring
    for (const player of session.players) {
      if (!player.deletedAt) {
        const roundWins = calculatePlayerRoundWins(session, player.id);
        scores.set(player.id, roundWins);
      }
    }
  }

  return scores;
};

/**
 * Calculate winner(s) based on game definition win condition
 */
export const calculateWinner = (
  session: Session,
  gameDefinition: GameConfig,
): {
  winners: PlayerOrTeamId[];
  isTie: boolean;
  scores: Map<PlayerOrTeamId, number>;
} => {
  const scores =
    gameDefinition.winCondition === 'mostPoints'
      ? calculateMostPointsScores(session, gameDefinition)
      : calculateMostRoundsScores(session, gameDefinition);

  const { winners } = findHighestScorers(scores);
  const isTie = winners.length > 1;

  return {
    winners,
    isTie,
    scores,
  };
};
